import { Injectable } from '@angular/core';
import { Observable, filter, map, take, timeout } from 'rxjs';
import { EventBusService } from './event-bus.service';
import { AllPluginActions, AllPluginMethods } from './plugin-registry';
import { ActionPayloads, PluginEvents } from './event-definitions';

// conditional type that enforces payload requirement
type ActionWithPayload<T extends AllPluginActions> = T extends keyof ActionPayloads
  ? ActionPayloads[T] extends undefined
    ? [action: T, payload?: undefined]
    : [action: T, payload: ActionPayloads[T]]
  : [action: T, payload?: any];

export interface PluginRequest<M extends AllPluginMethods = AllPluginMethods> {
  id: string;
  from: string;
  to: string;
  method: M;
  params?: any;
}

export interface PluginResponse<T = any> {
  id: string;
  from: string;
  to: string;
  result?: T;
  error?: any;
}

// method parameter definitions
export interface MethodParams {
  // "Astimax" plugin methods
  tryToggleVideoFullscreen: Record<string, never>;

  // "X" plugin methods
  //...
}

// method return type definitions
export interface MethodReturns {
  // "Astimax" plugin methods
  tryToggleVideoFullscreen: boolean;

  // "X" plugin methods
  //...
}

@Injectable({
  providedIn: 'root'
})
export class PluginCommunicationService {
  private requestCounter = 0;

  constructor(private eventBus: EventBusService) {}

  // send a typed action with strict compile-time checking
  sendAction<T extends keyof ActionPayloads>(
    from: string,
    action: T,
    ...args: ActionPayloads[T] extends undefined 
      ? [] 
      : [payload: ActionPayloads[T]]
  ): void {
    const payload = args[0];
    const [targetPlugin] = action.split(':');
    
    this.eventBus.emit(PluginEvents.PLUGIN_MESSAGE, {
      from,
      to: targetPlugin,
      action,
      data: payload
    });
  }

  // create a sender with pre-bound 'from' field for better ergonomics
  createSender(from: string) {
    return <T extends keyof ActionPayloads>(
      action: T,
      ...args: ActionPayloads[T] extends undefined 
        ? [] 
        : [payload: ActionPayloads[T]]
    ) => {
      this.sendAction(from, action, ...args);
    };
  }

  // listen for specific actions with type-safe payload
  onAction<T extends keyof ActionPayloads>(
    action: T,
    targetPlugin?: string
  ): Observable<ActionPayloads[T]> {
    return this.eventBus.on<any>(PluginEvents.PLUGIN_MESSAGE).pipe(
      filter(msg => 
        msg.action === action && 
        (!targetPlugin || msg.to === targetPlugin || !msg.to)
      ),
      map(msg => msg.data as ActionPayloads[T])
    );
  }

  // send a typed request with strict parameter checking
  request<M extends keyof MethodParams>(
    from: string,
    to: string,
    method: M,
    params: MethodParams[M],
    timeoutMs?: number
  ): Observable<M extends keyof MethodReturns ? MethodReturns[M] : never> {
    const requestId = `${from}_${++this.requestCounter}_${Date.now()}`;
    
    const request: PluginRequest<M> = {
      id: requestId,
      from,
      to,
      method,
      params
    };

    // listen for response before sending request
    const response$ = this.eventBus.on<PluginResponse>(PluginEvents.PLUGIN_RESPONSE).pipe(
      filter(response => response.id === requestId && response.from === to),
      take(1),
      timeout(timeoutMs || 5000)
    );

    // send request
    this.eventBus.emit(PluginEvents.PLUGIN_REQUEST, request);

    return new Observable(observer => {
      response$.subscribe({
        next: (response) => {
          if (response.error) {
            observer.error(response.error);
          } else {
            observer.next(response.result);
            observer.complete();
          }
        },
        error: (err) => {
          observer.error({
            message: `Request timeout: No response from ${to} for method ${method}`,
            originalError: err
          });
        }
      });
    });
  }

  // type-safe handler registration
  handleRequests<T extends {
    [K in keyof MethodParams]?: (
      params: MethodParams[K]
    ) => (K extends keyof MethodReturns ? MethodReturns[K] : any) | 
       Promise<K extends keyof MethodReturns ? MethodReturns[K] : any>
  }>(
    pluginName: string,
    handlers: T
  ): void {
    this.eventBus.on<PluginRequest>(PluginEvents.PLUGIN_REQUEST)
      .pipe(filter(request => request.to === pluginName))
      .subscribe(async (request) => {
        const handler = handlers[request.method as keyof T];
        
        const response: PluginResponse = {
          id: request.id,
          from: pluginName,
          to: request.from
        };

        if (!handler) {
          response.error = {
            code: 'METHOD_NOT_FOUND',
            message: `Method ${request.method} not found in ${pluginName}`
          };
        } else {
          try {
            response.result = await (handler as any)(request.params);
          } catch (error) {
            response.error = {
              code: 'HANDLER_ERROR',
              message: error instanceof Error ? error.message : 'Unknown error',
              details: error
            };
          }
        }

        this.eventBus.emit(PluginEvents.PLUGIN_RESPONSE, response);
      });
  }
}
